<!-- =========================================================================================
    File Name: AssignSOPModal.vue
    Description: Reusable component for assigning SOPs to users
    ----------------------------------------------------------------------------------------
    Item Name: TCVelik - Onboarding Platform
    Author: TCVelik Team
========================================================================================== -->

<template>
  <vs-popup 
    :title="title" 
    :active.sync="showModal" 
    fullscreen
    @vs-cancel="closeModal">
    
    <div class="assign-sop-modal-content">
      <div class="assign-sop-container">
        <h3 class="assign-sop-title">{{ title }}</h3>
        <div class="assign-sop-description" v-if="description">
          {{ description }}
        </div>
        
        <form @submit.prevent="submitAssignment" class="assign-sop-form">
          <!-- SOP Selection -->
          <div class="form-field">
            <label class="field-label">SOP</label>
            <SOPPicker 
              v-model="formData.selectedSOP"
            />
          </div>

          <!-- User Selection -->
          <div class="form-field">
            <label class="field-label">Assign To</label>
            <vs-select 
              v-model="formData.userId" 
              label="Select User"
              placeholder="Choose a user"
              class="field-input"
              required>
              <vs-select-item 
                v-for="user in availableUsers" 
                :key="user.id"
                :value="user.id"
                :text="`${user.firstName || ''} ${user.lastName || ''} (${user.email})`" />
            </vs-select>
            <div v-if="availableUsers.length === 0" class="text-center text-gray-500">
              No users available
            </div>
            <div v-else class="text-sm text-gray-600">
              Found {{ availableUsers.length }} user(s)
            </div>
          </div>
          
          <!-- Due Date -->
          <div class="form-field">
            <label class="field-label">Due Date</label>
            <vs-input 
              v-model="formData.dueDate"
              type="date"
              label="Due Date"
              placeholder="Select due date"
              class="field-input"
              required />
          </div>

          <!-- Allow User to Change Values -->
          <div class="form-field">
            <div class="checkbox-field">
              <vs-checkbox 
                v-model="formData.allowUserToChangeValues"
                label="Allow user to change values"
                class="field-input" />
            </div>
          </div>
          
          <div class="form-actions">
            <vs-button 
                            
              :disabled="!isFormValid">
              Create Assignment
            </vs-button>
            <vs-button 
              color="danger" 
              type="border" 
              size="large"
              @click="closeModal"
              class="ml-2">
              Cancel
            </vs-button>
          </div>
        </form>
      </div>
    </div>

  </vs-popup>
</template>

<script>
import TCVelikClient from '@/TCVelikClient/TCVelikClient.js';
import SOPPicker from '@/components/SOPPicker.vue';

export default {
  name: 'AssignSOPModal',
  components: {
    SOPPicker
  },
  props: {
    title: {
      type: String,
      default: 'Assign SOP'
    },
    description: {
      type: String,
      default: ''
    },
    prefilledDate: {
      type: String,
      default: null
    },
    prefilledSOP: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      showModal: false,
      formData: {
        userId: '',
        dueDate: null,
        selectedSOP: null,
        allowUserToChangeValues: false
      },
      availableUsers: []
    }
  },
  computed: {
    isFormValid() {
      return this.formData.userId && 
             this.formData.dueDate && 
             this.formData.selectedSOP;
    }
  },
  watch: {
    showModal(newVal) {
      if (newVal) {
        this.loadData();
        this.setPrefilledData();
      }
    }
  },
  methods: {
    async loadData() {
      await this.loadAvailableUsers();
    },

    setPrefilledData() {
      if (this.prefilledDate) {
        this.formData.dueDate = this.prefilledDate;
      }
      if (this.prefilledSOP) {
        this.formData.selectedSOP = this.prefilledSOP;
      }
    },

    async loadAvailableUsers() {
      try {
        const query = `
          query Users($skip: Int, $take: Int, $search: String, $sortType: String, $sortOrder: String!) {
            users(skip: $skip, take: $take, search: $search, sortType: $sortType, sortOrder: $sortOrder) {
              totalCount
              result {
                id
                firstName
                lastName
                email
                userName
              }
            }
          }
        `;

        const response = await TCVelikClient.PostRelative('/api/graphql', {
          query: query,
          variables: {
            skip: 0,
            take: 1000,
            search: '',
            sortType: 'lld',
            sortOrder: 'desc'
          }
        });

        if (response && response.data && response.data.data && response.data.data.users && response.data.data.users.result) {
          this.availableUsers = response.data.data.users.result;
        } else {
          this.availableUsers = [];
        }
      } catch (error) {
        console.error('Error loading users:', error);
        this.$vs.notify({
          title: 'Error',
          text: 'Failed to load users',
          color: 'danger'
        });
      }
    },


    async submitAssignment() {
      if (!this.isFormValid) {
        this.$vs.notify({
          title: 'Validation Error',
          text: 'Please fill in all required fields',
          color: 'danger'
        });
        return;
      }

      try {
        const mutation = `
          mutation AssignSchedule($onboardingId: Int!, $userId: String!, $dateDue: DateTime!, $type: Int!, $cadence: Int!, $customTitle: String) {
            assignSchedule(assignScheduleDTO: {
              onboardingId: $onboardingId,
              userId: $userId,
              dateDue: $dateDue,
              type: $type,
              cadence: $cadence,
              customTitle: $customTitle
            })
          }
        `;

        const response = await TCVelikClient.PostRelative('/api/graphql', {
          query: mutation,
          variables: {
            onboardingId: parseInt(this.formData.selectedSOP.id),
            userId: this.formData.userId,
            dateDue: new Date(this.formData.dueDate).toISOString(),
            type: 2, // COMPLETE_SOP type
            cadence: -1, // Custom date (use dateDue)
            customTitle: this.formData.selectedSOP.name
          }
        });

        if (response && response.data && response.data.data && response.data.data.assignSchedule) {
          this.$vs.notify({
            title: 'Success',
            text: 'SOP assignment created successfully!',
            color: 'success'
          });
          
          this.$emit('assignment-created', {
            sop: this.formData.selectedSOP,
            userId: this.formData.userId,
            dueDate: this.formData.dueDate
          });
          
          this.closeModal();
        } else {
          throw new Error('Failed to create assignment');
        }
      } catch (error) {
        console.error('Error creating SOP assignment:', error);
        this.$vs.notify({
          title: 'Error',
          text: 'Failed to create SOP assignment',
          color: 'danger'
        });
      }
    },

    closeModal() {
      this.showModal = false;
      this.resetForm();
    },

    resetForm() {
      this.formData = {
        userId: '',
        dueDate: null,
        selectedSOP: null,
        allowUserToChangeValues: false
      };
    },

    open() {
      this.showModal = true;
    }
  }
}
</script>

<style lang="scss" scoped>
.assign-sop-modal-content {
  padding: 20px;
  max-width: 600px;
  margin: 0 auto;
}

.assign-sop-container {
  width: 100%;
}

.assign-sop-title {
  font-size: 1.8rem;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 1rem;
  text-align: center;
}

.assign-sop-description {
  font-size: 1rem;
  color: #64748b;
  margin-bottom: 2rem;
  text-align: center;
  line-height: 1.5;
}

.assign-sop-form {
  width: 100%;
}

.form-field {
  margin-bottom: 1.5rem;
}

.field-label {
  display: block;
  font-size: 1rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
}

.field-input {
  width: 100%;
}

.form-actions {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 2rem;
}

.checkbox-field {
  margin-top: 0.5rem;
}

</style>
